import React, { useEffect } from "react";
import "./styles/OrderConfirmationScreen.css";
import { OrderConfirmationWrapper } from "./OrderConfirmationWrapper";
import { useOrderRepository } from "../../../data/repositories/OrderRepository";
import {
  ComponentState,
  useComponentStateMachine,
} from "../../shared/hooks/useComponentStateMachine";
import { LoadingView } from "../../components/loading-view/LoadingView";
import { ErrorView } from "../../components/error-view/ErrorView";
import { useSearchParams } from "react-router-dom";
import { useCartContext } from "../../../contexts/CartContext";

export function OrderConfirmationScreen(): React.ReactElement {
  const { state, dataTransition } = useComponentStateMachine();
  const { useOrder } = useOrderRepository();
  const { activeOrder, setActiveOrder } = useCartContext();

  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");
  const organizationId = searchParams.get("orgId");

  useEffect(() => {
    // Clear active order when unmounting the confirmation screen
    // This ensures that when leaving the page, the cart total in the nav bar
    // reverts to showing only the cart items total (without packaging/delivery fees)
    return () => {
      setActiveOrder(undefined);
    };
  }, []);

  // If we already have a locally built draft order, use it directly without fetching.
  // Ensure we don't show a stale draft if the URL requests a specific committed order.
  const shouldUseActiveOrder =
    activeOrder &&
    (!orderId || (activeOrder.id && activeOrder.id.toString() === orderId));

  if (shouldUseActiveOrder) {
    return <OrderConfirmationWrapper order={activeOrder} />;
  }

  const { data, isError, isLoading, isSuccess } = useOrder(
    +organizationId!,
    +orderId!
  );
  dataTransition(isLoading, isSuccess, isError);

  return state === ComponentState.Succeed ? (
    <OrderConfirmationWrapper order={data!} />
  ) : state === ComponentState.Loading ? (
    <LoadingView />
  ) : (
    <ErrorView />
  );
}
